/*

Usage Notes
    The component will grab the file that the user selects to upload and display a 
    preview to the screen. The component will return the selected File object to the
    parent through the setFile function. The parent will need to call the API to 
    upload the file to the server.

Parameters:
    - setFile: (Function)
        Function called to set the file that is being requested to upload
    - label?: (string) | default = 'File'
        Optional lable to display above upload 
    - imageSrc?: (string)
        Optional file source for display in preview
        
Example:
    <FileUpload setFile={fileFunction} />

*/
import React, { useState } from 'react'
import { Label } from '@progress/kendo-react-labels'
import { Upload, UploadFileInfo, UploadFileStatus, UploadListItemProps, UploadOnAddEvent, UploadOnRemoveEvent } from '@progress/kendo-react-upload'
import { ALLOWED_IMAGE_EXTENSIONS } from 'core/consts'
import { Form, FormClassComponent } from '@progress/kendo-react-form'
import { DeleteButton } from 'components'
import toast from 'core/toast'

export enum FileTypeEnum {
    Image = 0,
    Excel = 1,
    Pdf = 2,
    Video = 3
}

interface IProps {
    setFile: Function
    lable?: string
    imageSrc?: string
    formRef?: React.RefObject<FormClassComponent>
    name?: string
    showDelete?: boolean
    deleteImage?: Function
    disabled?: boolean
    allowedExtensions?: string[]
    fileType?: FileTypeEnum
    accept?: string[]
    maxFileSize?: number
    autoUpload?: boolean
    showActionButtons?: boolean
    showPreview?: boolean
    initialFileName?: string
}

const FileUpload = (props: IProps) => {

    const [imageSrc, setImageSrc] = useState(props.imageSrc ? props.imageSrc : '')
    const [fileName, setFileName] = useState<string | undefined>(undefined)
    const fileType = props.fileType ? props.fileType : FileTypeEnum.Image // Assume file will be an image unless passed in

    const onAdd = (event: UploadOnAddEvent) => {

        if (event.affectedFiles.length > 0) {
            if (event.affectedFiles[0].validationErrors && event.affectedFiles[0].validationErrors?.length > 0) {
                if (event.affectedFiles[0].validationErrors[0] === 'invalidFileExtension') {
                    toast.error('Selected file\'s extension is invalid')
                    props.setFile(undefined)
                    return
                }
                if (event.affectedFiles[0].validationErrors[0] === 'invalidMaxFileSize') {
                    if (props.maxFileSize) {
                        toast.error(`File size exceeds the maximum allowed size of ${props.maxFileSize / 1024 / 1024} MB`)
                    }
                    props.setFile(undefined)
                    return
                }
            }
        }

        const currentFile = event.affectedFiles[0] as UploadFileInfo
        const file: File = currentFile.getRawFile!()
        const reader = new FileReader()
        reader.addEventListener('load', function () {
            // convert image file to base64 string
            if (reader.result != undefined) {
                setImageSrc(reader.result?.toString())
            }
        }, false)

        if (file) {
            reader.readAsDataURL(file)
            if (fileType === FileTypeEnum.Video) {
                props.setFile(file)
            }
        }
    }

    const onRemove = (event: UploadOnRemoveEvent) => {
        props.setFile(undefined)
        setImageSrc('')
        setFileName(undefined)
    }

    const handleDelete = () => {
        props.setFile(undefined)
        setImageSrc('')
        setFileName(undefined)
        if (props.deleteImage)
            props.deleteImage()

    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onSaveRequest = (files: UploadFileInfo[], options: { formData: FormData, requestOptions: any }, onProgress: (uid: string, event: ProgressEvent<EventTarget>) => void) => {

        const saveRequestPromise = new Promise<{ uid: string }>((resolve, reject) => {
            try {
                const currentFile = files[0] as UploadFileInfo
                const uid = currentFile.uid
                const file: File = currentFile.getRawFile!()

                props.setFile(file)

                setFileName(file.name)
                if (props.formRef && props.formRef.current && props.name) {
                    props.formRef.current.onChange(props.name, { value: file.name })
                }
                resolve({ uid: uid })
            } catch (e) {

                reject('Upload Error')
            }
        })
        return saveRequestPromise
    }

    const defaultFile: UploadFileInfo[] = [{
        uid: '1', // Unique identifier for the file
        name: props.initialFileName || '', // File name
        validationErrors: [], // Validation errors (optional)
        status: UploadFileStatus.Uploaded, // File status
        progress: 100, // Upload progress (100 for fully uploaded files)
    }]

    return (
        <>
            {
                props.lable === ''
                    ?
                    <></>
                    :
                    <div>
                        <Label className='pt-3'>
                            {
                                props.lable === undefined ? 'File:' : `${props.lable}:`
                            }
                        </Label>
                    </div>
            }
            <Upload
                batch={false}
                defaultFiles={props.initialFileName ? defaultFile : []}
                autoUpload={props.autoUpload ?? true}
                showActionButtons={props.showActionButtons ?? true}
                multiple={false}
                saveUrl={onSaveRequest}
                onAdd={onAdd}
                onRemove={onRemove}
                restrictions={{
                    allowedExtensions: props.allowedExtensions ? props.allowedExtensions : ALLOWED_IMAGE_EXTENSIONS,
                    maxFileSize: props.maxFileSize ? props.maxFileSize : undefined,
                }}
                disabled={props.disabled != undefined ? props.disabled : false}
                accept={props.accept ? props.accept.join(',') : undefined}

            />

            {imageSrc
                && fileType === FileTypeEnum.Image
                && props.showPreview
                && <div className='text-center'>
                    <img src={imageSrc} className='img-fluid pt-2' style={{ width: '240px' }} />
                </div>
            }

            {imageSrc && props.showDelete && props.showDelete === true &&
                <div className='staff-delete text-center mx-3 mt-1 mb-2'>
                    <DeleteButton
                        id='staff-img-delete-btn'
                        className={'btn btn-sm staff-delete-btn'}
                        onClick={() => handleDelete()}
                    >
                        &nbsp;Remove Image
                    </DeleteButton>
                </div>
            }

        </>

    )

}

FileUpload.defaultProps = {
    showPreview: true
}

export default FileUpload